import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

// MODULES
import { jwtDecode } from "jwt-decode";

// TYPES
import { IHRAccountType } from "../../../App";

// HOOKS
import { useAuthContext } from "../../../hooks/context/AuthContext";

// STYLES
import "./Dashboard.scss";

// COMPONENTS
import { Helmet } from "react-helmet-async";
import GenericButton from "../../ui/buttons/GenericButtons/GenericButton";
import WithNavLayout from "../../layout/WithLayout/WithNavLayout";
import GoPremiumButton from "../../ui/buttons/SpecificCTAButtons/GoPremium/GoPremiumButton";

interface IDashboardProps {
    className?: string;
    accountType?: IHRAccountType;
    [key: string]: any;
}

const Dashboard: React.FC<IDashboardProps> = ({
    className = "",
    accountType,
    ...rest
}) => {
    const {
        token,
        email,
        isPremium,
        premiumFromStripe,
        activeStripeSub,
        //logout,
        getStripeSubscriptionInfo,
    } = useAuthContext();
    //const [tokenDisplayState, setTokenDisplayState] = useState<string>("");
    const navigate = useNavigate();

    const [userId, setUserId] = useState<string>("");
    const [accountIsPremium, setAccountIsPremium] = useState<boolean | null>(
        null,
    ); // if the user has access to premium regardless of if they have an active sub
    const [showGoPremiumBtn, setShowGoPremiumBtn] = useState(false); // only show if the user is free
    const [showManageSubscriptionBtn, setShowManageSubscriptionBtn] =
        useState(false); // only show if premium and premium from stripe sub and active sub is not already canceled

    useEffect(() => {
        if (isPremium !== null) {
            setAccountIsPremium(isPremium);
            if (isPremium) {
                // premium has been resolved and is no longer null - so we know go premium should not show
                // only show manage if there is an active stripe sub
                setShowGoPremiumBtn(false);
                if (isPremium && premiumFromStripe) {
                    setShowManageSubscriptionBtn(true);
                    // We don't care if the sub is cancelled
                    // if (activeStripeSub?.status !== "canceled") {
                    //     setShowManageSubscriptionBtn(true);
                    // } else {
                    //     setShowManageSubscriptionBtn(false);
                    // }
                } else {
                    // they have premium, but it is not a stripe premium so hide the manage option
                    setShowManageSubscriptionBtn(false);
                }
            } else {
                // the user is not premium - show go premium regardless
                setShowGoPremiumBtn(true);
            }
        }

        // show get premium if the user is not premium
    }, [
        isPremium,
        premiumFromStripe,
        activeStripeSub,
        activeStripeSub?.status,
    ]);

    useEffect(() => {
        if (!!token) {
            getStripeSubscriptionInfo(token);
            try {
                const decoded: { exp: number; user_id: string } =
                    jwtDecode(token);

                // if (!decoded.exp) {
                //     throw new Error("Missing exp in token");
                // }
                if (!decoded.user_id) {
                    throw new Error("Missing user_id in token");
                }

                // Remember! this is referring to the token expiration, not a subscription.
                // const expiryDate = new Date(decoded.exp * 1000); // JWT exp is in seconds
                // const formatted = expiryDate.toLocaleString(undefined, {
                //     dateStyle: "medium",
                //     timeStyle: "medium",
                // });

                const userId = decoded.user_id;
                setUserId(userId);

                //setTokenDisplayState(`Token expires on: ${formatted}`);
            } catch (err) {
                console.error("JWT decode error:", err);
                //setTokenDisplayState("error in jwt decode or missing exp");
            }
        } else {
            //setTokenDisplayState("error in jwt decode or token null");
        }
    }, [token]);

    useEffect(() => {
        // Reset scroll and layout
        document.documentElement.scrollLeft = 0;
        document.body.scrollLeft = 0;

        // Collapse any overflow left by the previous route
        document.documentElement.style.overflowX = "hidden";
        document.body.style.overflowX = "hidden";

        return () => {
            // Restore it on unmount if needed
            document.documentElement.style.overflowX = "";
            document.body.style.overflowX = "";
        };
    }, []);

    return (
        <>
            {/* TODO - replace when <Meta /> tag is available in react 19 */}
            <Helmet>
                <meta name="robots" content="noindex, nofollow" />
            </Helmet>
            <WithNavLayout>
                <div className={`Dashboard ${className}`} {...rest}>
                    <h2>Account</h2>
                    <div className="Dashboard__actions">
                        <GenericButton
                            text="View Shareable Reports"
                            onClick={() => navigate("/reports")}
                        />
                    </div>
                    <div className="Dashboard__info">
                        <p>
                            <strong>Email:</strong> {email}
                        </p>
                        <p>
                            <strong>Account Type:</strong>{" "}
                            {isPremium === null
                                ? ""
                                : isPremium
                                ? "Premium"
                                : "Free"}
                        </p>
                        {/* {process.env.REACT_APP_ENV !== "staging" ? (
                            <></>
                        ) : (
                            <p>
                                <strong>Premium From Stripe:</strong>{" "}
                                {premiumFromStripe ? "True" : "False"}
                            </p>
                        )} */}
                        {/* {!isPremium ? (
                            <GoPremiumButton
                                onClick={() => {
                                    navigate(`/go-premium/${userId}`);
                                }}
                            />
                        ) : (isPremium && !premiumFromStripe) ||
                          (isPremium &&
                              premiumFromStripe &&
                              activeStripeSub?.status === "canceled") ? (
                            <></>
                        ) : (
                            <GenericButton
                                className="Dashboard__ManageSubscription"
                                text="Manage Subscription"
                                onClick={() => navigate("/manage-subscription")}
                            />
                        )} */}
                        {showGoPremiumBtn ? (
                            <GoPremiumButton
                                onClick={() => {
                                    navigate(`/go-premium/${userId}`);
                                }}
                            />
                        ) : (
                            <></>
                        )}
                        {showManageSubscriptionBtn ? (
                            <GenericButton
                                className="Dashboard__ManageSubscription"
                                text="Manage Subscription"
                                onClick={() => navigate("/manage-subscription")}
                            />
                        ) : (
                            <></>
                        )}
                    </div>
                </div>
            </WithNavLayout>
        </>
    );
};

export default Dashboard;
